import {
	BackgroundTypes,
	MapPosition,
	MapSelectedAssets,
} from '@innovyze/inno-map';
import {
	STYLOVYZE_FLAGS,
	useIsFeatureEnabled,
	useSettings,
	useSiteConfig,
} from '@innovyze/stylovyze';
import {
	getMapBackground,
	getMapPosition,
	getMapSelected,
	getTenant,
} from '@innovyze/lib_am_common/Selectors';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { mapStateUrl } from '@innovyze/lib_am_common/Actions';
import { useDebounce } from '@Hooks';

export interface MapState {
	position?: MapPosition;
	background: BackgroundTypes;
	selected: MapSelectedAssets;
	url: string;
}

interface Asset {
	assetId: string;
	assetType?: string;
}

// There is an issue where by using encodeURI to encode %
// it is removed by the time it goes into the url so
// using custom encoding instead
const encodeForUrl = (str: string) => {
	return str.replace(/%/g, '+0');
};

const decodeFromUrl = (str: string) => {
	return str.replace(/\+0/g, '%');
};

const idAssetType = (id: string): Asset => {
	if (!id.match('#')) return { assetId: id };
	const [assetType, assetId] = id.split('#');
	return {
		assetType,
		assetId: `~${encodeForUrl(assetId)}`,
	};
};

const properId = (assetId: string, assetType: string): string => {
	if (!assetId.match(/^~/)) return assetId;
	return `${assetType}#${decodeFromUrl(assetId.replace(/^~/, ''))}`;
};

export const formatSelected = (selected: MapState['selected']): string => {
	const grouped = selected
		.map(({ id, ...other }) => {
			const { assetType, assetId } = idAssetType(id);
			return {
				...other,
				assetType,
				assetId,
			};
		})
		.reduce((prev, current) => {
			const { layerId, assetType, assetId } = current;
			const key =
				!assetType || layerId === assetType
					? layerId
					: `${layerId}:${assetType}`;
			return {
				...prev,
				// See comments in package.md wrt eslint-disable-line below
				[key]: [...(prev[key] || []), assetId], // eslint-disable-line
			};
		}, {} as { [key: string]: string[] });
	return Object.entries(grouped)
		.map(([key, value]) => `${key}=${value.join(',')}`)
		.join('&');
};

export const selectedStateChanged = (
	a: MapState['selected'],
	b: MapState['selected'],
): boolean => {
	if (a.length !== b.length) return true;
	for (const value of Object.values(a)) {
		const foundInB = b.find(
			({ id, layerId }) => id === value.id && layerId === value.layerId,
		);
		if (!foundInB) return true;
	}
	for (const value of Object.values(b)) {
		const foundInA = a.find(
			({ id, layerId }) => id === value.id && layerId === value.layerId,
		);
		if (!foundInA) return true;
	}
	return false;
};

export const extractSelected = (urlSelected: string): MapState['selected'] => {
	const groups = urlSelected.split('&');
	const selected = groups.flatMap(group => {
		const [key, value] = group.split('=');
		const [layerId, assetType] = key.split(':');
		const assets = value.split(',');
		return assets.map(assetId => ({
			layerId,
			id: properId(assetId, assetType || layerId),
		}));
	});
	return selected;
};

const numberLike = '(-?[0-9.]{1,})';

export const isNumberLike = (num: string): boolean => {
	return new RegExp(`^${numberLike}$`).test(num);
};

export const isLatLongLike = (coords: string): boolean => {
	return new RegExp(`^${numberLike},${numberLike}$`).test(coords);
};

export const isBackgroundLike = (background: string): boolean => {
	return Object.values(BackgroundTypes).includes(
		background as BackgroundTypes,
	);
};

export const isSelectedLike = (selected: string): boolean => {
	return new RegExp('^([A-z-:]{1,})=').test(selected);
};

export const isLayersLike = (layers: string): boolean => {
	return (
		new RegExp('^([A-z0-9,\\.-]{1,})$').test(layers) &&
		!isNumberLike(layers)
	);
};

export const getParamsFromURL = (url: string) => {
	const params = decodeURIComponent(url)
		.replace(/^\//, '')
		.split('/')
		.filter(value => !value.match(/^map#?$/));

	let center, zoom, background, selected;
	for (let i = 0; i < params.length; i++) {
		const param = params[i];
		if (isLatLongLike(param)) {
			center = param;
		} else if (isNumberLike(param)) {
			zoom = param;
		} else if (isBackgroundLike(param)) {
			background = param;
		} else if (isSelectedLike(param)) {
			selected = param;
		}
	}

	let urlPosition = null;
	if (center && zoom) {
		const urlCenter = center.split(',').map(item => +item);
		const urlZoom = +zoom;
		urlPosition = {
			center: urlCenter,
			zoom: urlZoom,
		};
	}

	const urlBackground = background as MapState['background'];

	let urlSelected: MapState['selected'] = [];
	if (selected && selected !== '' && selected !== '-') {
		urlSelected = extractSelected(selected);
	}
	return { urlPosition, urlBackground, urlSelected };
};

export const formatUrl = ({
	background = BackgroundTypes.Streets,
	position,
	selected,
}: Omit<MapState, 'url'>) => {
	const url = ['map', background];
	if (position) {
		url.push(position.center.toString());
		url.push(position.zoom.toString());
	}

	if (!selected.length) {
		url.push('-');
	} else if (selected.length) {
		url.push(formatSelected(selected));
	}

	return `/map#/${url.join('/')}`;
};

export const useMapState = () => {
	const history = useHistory();
	const location = useLocation();
	const dispatch = useDispatch();

	const storePosition: MapPosition = useSelector(getMapPosition);
	const storeBackground: BackgroundTypes = useSelector(getMapBackground);
	const storeSelected: MapSelectedAssets = useSelector(getMapSelected);
	const tenantId: string = useSelector(getTenant);
	const showArcGISStyles = !!useIsFeatureEnabled(
		STYLOVYZE_FLAGS.ARCGIS_BASEMAPS,
	);
	const { config } = useSiteConfig();
	const {
		companySettings: { mapSettings },
	} = useSettings();
	const arcGISToken = showArcGISStyles
		? config?.mapbox?.arcGISKey ?? undefined // eslint-disable-line
		: undefined;
	const basemapOrigin = arcGISToken
		? mapSettings?.basemapOrigin ?? 'mapbox'
		: 'mapbox';

	const defaultBackground = useMemo(
		() =>
			basemapOrigin == 'arcgis'
				? BackgroundTypes.OSMStandard
				: BackgroundTypes.Streets,
		[basemapOrigin],
	);

	const urlPath = location.pathname + (location.hash ?? ''); // eslint-disable-line

	const { urlPosition, urlBackground, urlSelected } =
		getParamsFromURL(urlPath);

	// load from cache if navigating to route map page and not direct link
	const loadCache = urlPath.match(/^\/?map\/?$/);

	let initState = {
		position: urlPosition ?? undefined,
		background: urlBackground ?? defaultBackground, // eslint-disable-line
		selected: urlSelected,
		url: urlPath,
	};

	if (loadCache && storePosition) { // eslint-disable-line
		initState = {
			position: storePosition,
			background: storeBackground ?? defaultBackground, // eslint-disable-line
			selected: storeSelected,
			url: urlPath,
		};
	}

	const mapStateWithBackgroundFallback = (state: MapState) => ({
		...state,
		background: state?.background ?? defaultBackground, // eslint-disable-line
	});

	const [state, setState] = useState<MapState>(
		mapStateWithBackgroundFallback(initState),
	);

	const updateState = (newState: Partial<MapState>) => {
		setState(prevState => {
			const newStateWithBGFallback = mapStateWithBackgroundFallback({
				...prevState,
				...newState,
			});
			const url = formatUrl(newStateWithBGFallback);
			return { ...newStateWithBGFallback, url };
		});
	};

	useEffect(() => {
		dispatch(mapStateUrl({ ...state, tenantId }));
	}, [state]);

	const debouncedUrl = useDebounce(state.url, 200);

	const setPosition = (newPosition: MapState['position']) => {
		updateState({
			position: newPosition,
		});
	};

	const setBackground = (newBackground: MapState['background']) => {
		updateState({ background: newBackground });
	};

	const setSelected = (newSelected: MapState['selected']) => {
		updateState({ selected: newSelected });
	};

	useEffect(() => {
		if (urlPath !== debouncedUrl) {
			history.push(debouncedUrl);
		}
	}, [debouncedUrl]);

	useEffect(() => {
		if (urlPath !== debouncedUrl) {
			// prevent map resetting when clicking on the map link whilst on the map
			if (loadCache) {
				history.replace(state.url);
				return;
			}
			let newState = mapStateWithBackgroundFallback({
				...state,
				url: urlPath,
			});

			if (urlBackground !== state.background) {
				newState = { ...newState, background: urlBackground };
			}

			if (
				`${urlPosition?.center}` !== `${state?.position?.center}` || // eslint-disable-line
				urlPosition?.zoom !== state?.position?.zoom // eslint-disable-line
			) {
				newState = { ...newState, position: urlPosition ?? undefined };
			}

			if (selectedStateChanged(urlSelected, state.selected)) {
				newState = mapStateWithBackgroundFallback({
					...newState,
					selected: urlSelected,
				});
			}
			updateState(newState);
		}
	}, [urlPath]);

	useEffect(() => {
		if (loadCache) {
			// update url to match state loaded from cache
			const url = formatUrl(mapStateWithBackgroundFallback(state));
			history.replace(url);
		} else {
			// update redux store to save state from url
			updateState(initState);
			// react router dom removes the hash when the page is reloaded
			// added it back in so that if page is refreshed again
			// it won't get the long url issue
			if (!urlPath.match('#')) {
				const url = formatUrl(initState);
				history.replace(url);
			}
		}
	}, []);

	useEffect(() => {
		setBackground(defaultBackground);
	}, [mapSettings?.basemapOrigin]);

	return {
		...state,
		setPosition,
		setBackground,
		setSelected,
		basemapOrigin,
	};
};
